import {
  isEmergencyDepartment,
  isUrgentCare,
} from "../../constants/facilityTypes";

/**
 * CareMap ED display rules (Phase 4): federal/restricted toggle + pediatric labels.
 * Pure functions — safe for unit tests without Firestore.
 */

/** @typedef {'military' | 'va' | 'ihs'} AccessRestrictionSubtype */

export const ACCESS_RESTRICTION_LABELS = Object.freeze({
  military: "Military facility — limited public access",
  va: "VA — limited public access",
  ihs: "IHS — limited public access",
});

/**
 * @param {string | null | undefined} accessRestriction
 * @returns {string | null}
 */
export function getAccessRestrictionLabel(accessRestriction) {
  if (!accessRestriction) return null;
  return ACCESS_RESTRICTION_LABELS[accessRestriction] ?? null;
}

/**
 * @param {object | null | undefined} location
 * @returns {boolean}
 */
export function hasAccessRestriction(location) {
  const ar = location?.accessRestriction;
  return ar === "military" || ar === "va" || ar === "ihs";
}

/**
 * Effective filter after SCMap embed override (restricted always hidden on /scmap).
 *
 * @param {object} filter
 * @param {{ forceHideRestrictedAccess?: boolean }} [options]
 * @returns {object}
 */
export function resolveMapFilter(filter, options = {}) {
  if (options.forceHideRestrictedAccess) {
    return { ...filter, showRestrictedAccess: false };
  }
  return filter;
}

/**
 * Normalize group name for URL comparison (matches Marker.js).
 *
 * @param {string | null | undefined} name
 * @returns {string}
 */
export function normalizeGroupForUrl(name) {
  if (!name) return "";
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s\-'&]/g, "")
    .replace(/[\s'&]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Whether a facility marker should render for the current filter (Marker isVisible rules).
 *
 * @param {object} data — location doc fields used by the map
 * @param {object} filter — { facility, rating, group, specialty, showRestrictedAccess }
 * @param {{ forceHideRestrictedAccess?: boolean }} [options]
 * @returns {boolean}
 */
export function isMapMarkerVisible(data, filter, options = {}) {
  const effectiveFilter = resolveMapFilter(filter, options);

  if (hasAccessRestriction(data) && !effectiveFilter.showRestrictedAccess) {
    return false;
  }

  if (effectiveFilter.group && normalizeGroupForUrl(data.group) !== effectiveFilter.group) {
    return false;
  }

  const ratingOk =
    effectiveFilter.rating === 0 || data.rating >= effectiveFilter.rating;

  if (effectiveFilter.facility === "emergency") {
    return isEmergencyDepartment(data) && ratingOk;
  }

  if (effectiveFilter.facility === "urgent_care") {
    return isUrgentCare(data) && ratingOk;
  }

  if (effectiveFilter.facility === "clinic") {
    if (isEmergencyDepartment(data) || isUrgentCare(data)) {
      return false;
    }
    if (effectiveFilter.specialty) {
      const locationSpecialties = data.specialties || [];
      if (!locationSpecialties.includes(effectiveFilter.specialty)) {
        return false;
      }
    }
    return ratingOk;
  }

  return ratingOk;
}

/**
 * Suffix for marker title / tooltip (pediatric + restricted labels).
 *
 * @param {object} data
 * @returns {string}
 */
export function getMapMarkerLabelSuffix(data) {
  const parts = [];
  if (data.pediatricOnly) {
    parts.push("Children's hospital");
  }
  const restrictionLabel = getAccessRestrictionLabel(data.accessRestriction);
  if (restrictionLabel) {
    parts.push(restrictionLabel);
  }
  return parts.length ? ` · ${parts.join(" · ")}` : "";
}

/**
 * Full hover title for a map marker.
 *
 * @param {object} data
 * @returns {string}
 */
export function buildMapMarkerTitle(data) {
  const title = data.title || "";
  return `${title}${getMapMarkerLabelSuffix(data)}`;
}
